import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  WEB3FORMS_ACCESS_KEY,
  isWeb3FormsConfigured,
  CONTACT_EMAIL,
  ENQUIRY_CATEGORIES,
} from '../config/site'

type Status = 'idle' | 'submitting' | 'success' | 'error'

function Contact() {
  const configured = isWeb3FormsConfigured()
  const [status, setStatus] = useState<Status>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    data.append('access_key', WEB3FORMS_ACCESS_KEY)
    // Use `||` (not `??`): the select contributes an empty string when no
    // category is chosen, so `??` would leave a blank subject — `||` falls back
    // to "General" for both the empty-string and missing cases.
    data.append('subject', `Stratum Labs enquiry: ${data.get('category') || 'General'}`)

    setStatus('submitting')
    setErrorMessage('')
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data,
      })
      const json = (await res.json()) as { success: boolean; message?: string }
      if (res.ok && json.success) {
        setStatus('success')
        form.reset()
      } else {
        setStatus('error')
        setErrorMessage(json.message ?? 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setErrorMessage('Network error. Please try again, or email us directly.')
    }
  }

  return (
    <>
      <section className="hero hero--compact">
        <div className="container">
          <h1>Contact us</h1>
          <p className="tagline">
            Tell us what you need and we&apos;ll get back to you. Choose a category so your enquiry
            reaches the right place.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container container--narrow">
          {!configured && (
            <p className="notice" role="status">
              Our enquiry form is being finalised. In the meantime, email us directly at{' '}
              <a className="link" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          )}

          {status === 'success' ? (
            <div className="notice notice--success" role="status">
              <p>Thanks — your enquiry has been sent. We&apos;ll be in touch shortly.</p>
              <p className="notice__actions">
                <button
                  className="btn btn--primary"
                  type="button"
                  onClick={() => {
                    setErrorMessage('')
                    setStatus('idle')
                  }}
                >
                  Send another enquiry
                </button>
                <Link className="btn btn--ghost" to="/">
                  Back to home
                </Link>
              </p>
            </div>
          ) : (
            <form className="form" onSubmit={handleSubmit} noValidate>
              <label className="form__field">
                <span className="form__label">Name</span>
                <input
                  className="form__input"
                  type="text"
                  name="name"
                  required
                  autoComplete="name"
                />
              </label>

              <label className="form__field">
                <span className="form__label">Email</span>
                <input
                  className="form__input"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                />
              </label>

              <label className="form__field">
                <span className="form__label">Category</span>
                <select
                  className="form__input"
                  name="category"
                  required
                  defaultValue=""
                  aria-describedby="category-hint"
                >
                  <option value="" disabled>
                    Select an enquiry type…
                  </option>
                  {ENQUIRY_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <p id="category-hint" className="form__hint form__hint--category">
                Please keep this enquiry to the selected category only. Mixing
                topics into one message slows routing and increases wait times —
                if you have more than one, send a separate enquiry per category
                (you can use “Send another enquiry” after submitting).
              </p>

              <label className="form__field">
                <span className="form__label">Message</span>
                <textarea className="form__input" name="message" rows={6} required />
              </label>

              {status === 'error' && (
                <p className="notice notice--error" role="alert">
                  {errorMessage}{' '}
                  <a className="link" href={`mailto:${CONTACT_EMAIL}`}>
                    Email us instead
                  </a>
                  .
                </p>
              )}

              {configured ? (
                <button
                  className="btn btn--primary"
                  type="submit"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Sending…' : 'Send enquiry'}
                </button>
              ) : (
                <a
                  className="btn btn--primary"
                  href={`mailto:${CONTACT_EMAIL}?subject=Stratum%20Labs%20enquiry`}
                >
                  Email your enquiry
                </a>
              )}
            </form>
          )}
        </div>
      </section>
    </>
  )
}

export default Contact
